'use client';
import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { imagePath } from '../../utils/imagePath';
import { useNavigate } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

/* ==============================
   CONTENT
============================== */
const content = [
  {
    title: 'Training, Safety & Performance',
    path: '/all-industries/raghva-life-sciences',
    percentage: '60%',
    logo: imagePath.LifeSciences,
    descTitle: 'Reduction in error rates.',
    desc: 'Immersive simulations strengthen training outcomes by improving hazard recognition, sharpening decision-making, reinforcing correct procedures, and reducing on-the-job errors.',
    videoSrc: '/assets/images/video/home/TrainingSafetyperformance.mp4',
  },
  {
    title: 'Education Tech',
    path: '/all-industries/corpus-codex',
    percentage: '43%',
    logo: imagePath.CorpusCodex,
    descTitle: 'Increase in retention rates.',
    desc: 'Immersive learning boosts retention by increasing engagement, improving comprehension, enabling hands-on practice, and helping educators explain complex topics clearly.',
    videoSrc: '/assets/images/video/home/EducationTech.mp4',
  },
  {
    title: 'Sales & Marketing',
    path: '/all-industries/americana',
    percentage: '65%',
    logo: imagePath.BrandPower,
    descTitle: 'Boost in engagement rates.',
    desc: 'Immersive experiences boost conversions by increasing engagement, improving product understanding, reducing decision friction, and generating better leads.',
    videoSrc: '/assets/images/video/home/SaleMarketing.mp4',
  },
  {
    title: 'Asset Simulation',
    path: '/all-industries/medha-servo-drives',
    percentage: '90%',
    logo: imagePath.Medha,
    descTitle: 'Increase in Training Effectiveness',
    desc: 'Simulation-based training boosts accuracy in operation, safety, navigation, and procedures by allowing personnel to repeat tasks without real-world risk.',
    videoSrc: '/assets/images/video/home/AssetSimulation.mp4',
  },
  {
    title: 'Design & Prototyping',
    path: '/platform-overview',
    percentage: '60%',
    logo: imagePath.Company12,
    descTitle: 'Reduction in product development time.',
    desc: 'Immersive prototyping accelerates design cycles by enabling rapid iteration, real-time visualization, and early issue detection.',
    videoSrc: '/assets/images/video/home/DesignPrototyping.mp4',
  },
];

export default function XRSolutionsScrollCards() {
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const cardsRef = useRef([]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  /* ==============================
     SCREEN SIZE DETECTION
  ============================== */
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /* ==============================
     GSAP SCROLL ANIMATION
  ============================== */
  useEffect(() => {
    const cards = cardsRef.current.filter(Boolean);
    if (!containerRef.current || !cards.length) return;

    // Kill existing ScrollTriggers
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

    // Configuration
    const cardCount = content.length;
    const scrollDistance = isMobile ? 1200 : 1500;
    const totalScrollHeight = scrollDistance * (cardCount - 1);

    // Set initial state - all cards positioned at center
    cards.forEach((card, index) => {
      gsap.set(card, {
        opacity: index === 0 ? 1 : 0,
        y: 0,
        scale: 1,
        zIndex: index === 0 ? 10 : 1,
      });
    });

    // Create main ScrollTrigger
    ScrollTrigger.create({
      trigger: containerRef.current,
      start: 'top top',
      end: `+=${totalScrollHeight}`,
      pin: cardsContainerRef.current,
      pinSpacing: true,
      scrub: 1,
      onUpdate: (self) => {
        const progress = self.progress;
        const currentIndex = Math.min(
          Math.floor(progress * cardCount),
          cardCount - 1
        );

        setActiveIndex(currentIndex);

        // Animate cards based on scroll progress
        cards.forEach((card, index) => {
          if (index === currentIndex) {
            // Active card - fully visible, centered
            gsap.to(card, {
              opacity: 1,
              y: 20,
              scale: 1,
              zIndex: 10,
              duration: 0.1,
              ease: 'power2.out',
            });
          } else if (index < currentIndex) {
            // Previous cards - slide up and fade out
            gsap.to(card, {
              opacity: 0,
              y: -100,
              scale: 0.9,
              zIndex: 1,
              duration: 0.1,
              ease: 'power2.inOut',
            });
          } else {
            // Upcoming cards - hidden, ready to appear
            gsap.to(card, {
              opacity: 0,
              y: 0,
              scale: 0.9,
              zIndex: 1,
              duration: 0.1,
              ease: 'power2.inOut',
            });
          }
        });
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [isMobile]);

  return (
    <div ref={containerRef} className="relative w-full text-white">
      {/* CARDS CONTAINER */}
      <div
        ref={cardsContainerRef}
        className="relative w-full h-screen flex items-center justify-center"
      >
        {/* DOT INDICATOR */}
        <div className="fixed hidden md:flex right-6 top-1/2 -translate-y-1/2 flex-col gap-3 z-20">
          {content.map((_, i) => (
            <div
              key={i}
              className={`h-2.5 w-2.5 rounded-full transition-all duration-300 ${
                i === activeIndex ? 'bg-primary scale-125' : 'bg-[#171616]'
              }`}
            />
          ))}
        </div>

        {/* STACKED CARDS */}
        <div className="relative w-full max-w-[88rem] mx-auto px-4">
          <div className="relative w-full h-[500px] md:h-[600px] lg:h-[600px]">
            {content.map((item, i) => (
              <div
                key={i}
                ref={(el) => {
                  if (el) cardsRef.current[i] = el;
                }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full"
              >
                <div className="bg-[#171616] rounded-2xl overflow-hidden shadow-2xl w-[92%] mx-auto h-auto md:h-[720px] lg:h-[550px]">
                  <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                    {/* LEFT CONTENT */}
                    <div className="p-6 sm:p-8 lg:p-12 flex flex-col h-full">
                      <div>
                        <img
                          src={item.logo}
                          alt="logo"
                          className="h-10 mb-6 object-contain"
                        />

                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-telegraf">
                          {item.title}
                        </h3>
                      </div>

                      <div className="flex flex-col gap-4 mt-8">
                        <div className="text-5xl sm:text-6xl font-extrabold bg-primary text-transparent bg-clip-text border-b border-[#686565] pb-3">
                          {item.percentage}
                        </div>

                        <div className="text-xl lg:text-2xl font-semibold">
                          {item.descTitle}
                        </div>

                        <p className="text-gray-300 leading-relaxed text-sm lg:text-md">
                          {item.desc}
                        </p>
                      </div>

                      {/* CTA pinned bottom */}
                      <div
                        onClick={() => navigate(item.path)}
                        className="mt-auto pt-6 flex items-center gap-2 text-primary text-[20px] cursor-pointer group"
                      >
                        <span>See Full use cases</span>

                        {/* ARROW SVG */}
                        <svg
                          width="20"
                          height="16"
                          viewBox="0 0 26 16"
                          fill="none"
                          className=""
                        >
                          <path
                            d="M0 8H24"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          />
                          <path
                            d="M18 1L25 8L18 15"
                            stroke="currentColor"
                            strokeWidth="3.5"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* RIGHT VIDEO */}
                    <div className="relative h-full p-4">
                      <video
                        src={item.videoSrc}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full md:h-[28vh] lg:h-full rounded-xl object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
